import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { CButton, CForm, CFormFeedback, CFormInput, CInputGroup } from '@coreui/react-pro';
import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { tryParseJSON } from '../../utils';
import { isAuth } from '../../helpers/auth';
import { checkAcceptSeatLayout } from '../../helpers/subscriber';
import { GeneralAPI } from '../../services/apis/generalAPI';

import {
     CAMS_CARE_URL,
     GUEST_PATH,
     LOCAL_ORG_USER,
     MODAL_LOGIN,
     MY_LEARNING_PATH,
     MY_ORG_PATH,
     REGEX_EMAIL,
     SUBSCRIBER_PATH,
     SYSTEM_ADMIN_ROUTES,
     USER_ROLES,
} from '../../constants';
import images from '../../assets/images';
import Button from '../../components/General/Button';

import { setShow, setSidebarShow, toggleTheme } from '../../actions/general';

const validationSchema = () =>
     Yup.object().shape({
          email: Yup.string().required('Email is required').matches(REGEX_EMAIL, 'Not a valid email'),
     });

const GuestFooter = () => {
     const location = useLocation();

     const locationPath = location.pathname;
     const isAcceptSeatLayout = checkAcceptSeatLayout(locationPath);

     const dispatch = useDispatch();
     const user = useSelector((state) => state.subscriber.user);
     const contentManagements = useSelector((state) => state.subscriber.contentManagements);

     const [windowSize, setWindowSize] = useState(getWindowSize());

     const userRole = user?.role;
     const adminRole = isAuth() && (userRole === USER_ROLES.SYSTEM_ADMIN || userRole === USER_ROLES.SYSTEM_ADMIN_LOWER);
     const isAuthNoAdmin = isAuth() && !adminRole;
     const paramProduct = locationPath === GUEST_PATH.SELLING_GROUPS || location.pathname.includes(GUEST_PATH.SELLING_GROUPS);
     const orgUser = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const myAccountPath = adminRole ? SYSTEM_ADMIN_ROUTES : orgUserLocalStorage ? MY_ORG_PATH.ORGANIZATIONS_DASHBOARD : MY_LEARNING_PATH.DASHBOARD;

     const pageSellingGroupTrainingCalendarLink = contentManagements.find((content) => content.key === 'pageSellingGroupTrainingCalendarLink');

     const initialValues = {
          email: '',
     };

     useEffect(() => {
          const handleWindowResize = () => {
               setWindowSize(getWindowSize());
          };
          window.addEventListener('resize', handleWindowResize);

          return () => {
               window.removeEventListener('resize', handleWindowResize);
          };
     }, []);

     const isMobile = useMemo(() => {
          return windowSize.innerWidth <= 767;
     }, [windowSize]);

     function getWindowSize() {
          const { innerWidth } = window;
          return { innerWidth };
     }

     const listenLayerGfFormFailed = () => {
          window.ListenlayerCustomEvent?.gfFormFailed({
               formId: '84',
               formFail: {
                    input_84_1: {
                         id: 'input_84_1',
                         label: '',
                         name: 'email',
                         type: 'text',
                         placeholder: 'Enter email',
                         errormessage: 'Email is required',
                    },
               },
          });
     };

     const listenLayerGfFormSubmitted = (email) => {
          window.ListenlayerCustomEvent?.gfFormSubmitted({
               formId: '84',
               formValues: {
                    input_84_1: {
                         id: 'input_84_1',
                         value: email,
                         name: 'email',
                         type: 'text',
                         placeholder: 'Enter email',
                    },
               },
          });
     };

     const handleValidationForm = async (values) => {
          try {
               await validationSchema().validate(values, { abortEarly: false });
               // Validation succeeded
          } catch (error) {
               // Validation failed
               listenLayerGfFormFailed();
          }
     };

     const handleSubmitForm = async ({ email }, { setSubmitting, resetForm }) => {
          try {
               const payload = {
                    email: email.toLowerCase(),
               };
               listenLayerGfFormSubmitted(payload.email);

               const isProductionEnv = process.env.REACT_APP_ENV === 'prod';
               if (isProductionEnv) {
                    resetForm();
                    return;
               }

               const result = await GeneralAPI.signUpCamsCareEmailGravityForm(payload);

               if (!result) throw new Error('Submit form failed');

               resetForm();
               toast.success(result.message);
          } catch (error) {
               listenLayerGfFormFailed();
          } finally {
               setSubmitting(false);
          }
     };

     return (
          <footer className="footer">
               <div className="container">
                    <div className="footer-top">
                         <div className="footer-left">
                              <div className="footer-main-logo-wrapper">
                                   {(isAuthNoAdmin && paramProduct) || paramProduct ? (
                                        <a href={CAMS_CARE_URL} rel="noopener noreferrer" className="card-link">
                                             <img src={images.logoFooter} alt="CAMS Care Logo" className="footer-logo" />
                                        </a>
                                   ) : isAuthNoAdmin ? (
                                        <Link to={orgUser ? MY_ORG_PATH.ORGANIZATIONS_DASHBOARD : MY_LEARNING_PATH.DASHBOARD}>
                                             <img src={images.logoFooter} alt="CAMS Care Logo" className="footer-logo" />
                                        </Link>
                                   ) : (
                                        <Link to={SUBSCRIBER_PATH.SELLING_GROUPS}>
                                             <img src={images.logoFooter} alt="CAMS Care Logo" className="footer-logo" />
                                        </Link>
                                   )}
                              </div>
                              <div className="footer-menu">
                                   {!isAcceptSeatLayout &&
                                        (isAuth() ? (
                                             <Link to={myAccountPath} className="footer-menu-item item-my-account">
                                                  My Account
                                             </Link>
                                        ) : (
                                             <CButton
                                                  color="link"
                                                  className="footer-menu-item"
                                                  onClick={() => {
                                                       dispatch(setShow(MODAL_LOGIN));
                                                  }}
                                             >
                                                  Sign in
                                             </CButton>
                                        ))}
                                   <a
                                        href={pageSellingGroupTrainingCalendarLink?.value || ''}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-menu-item item-training-calendar"
                                   >
                                        Training Calendar
                                   </a>
                                   <div className="footer-menu-item-wrap">
                                        <a href={CAMS_CARE_URL} target="_blank" rel="noopener noreferrer" className="footer-link-item item-cams-care">
                                             CAMS-care.com
                                        </a>
                                        <a
                                             href={`${CAMS_CARE_URL}contact-us/`}
                                             target="_blank"
                                             rel="noopener noreferrer"
                                             className="footer-link-item item-contact"
                                        >
                                             Contact Us
                                        </a>
                                   </div>
                              </div>
                         </div>

                         <div className="footer-right">
                              <div className="footer-right-wrapper">
                                   <h4 className="footer-right-title">Stay Connected</h4>
                                   {isMobile && <p className="footer-right-content">Sign up to receive updates! Unsubscribe at anytime</p>}
                                   {!isMobile && (
                                        <p className="footer-right-content">
                                             Join our growing community committed to decreasing suffering and treating suicidal risk. Subscribe to the
                                             CAMS Connection today to receive the latest updates, news, and resources.
                                        </p>
                                   )}
                                   <div className="footer-gform_wrapper">
                                        <Formik
                                             initialValues={initialValues}
                                             validationSchema={validationSchema}
                                             validate={handleValidationForm}
                                             onSubmit={handleSubmitForm}
                                             validateOnChange={false}
                                             validateOnBlur={false}
                                        >
                                             {({ values, errors, handleSubmit, isSubmitting, setFieldValue, setFieldError }) => {
                                                  const handleChangeEmail = (e) => {
                                                       setFieldValue('email', e.target.value.trim());
                                                       setFieldError('email', undefined);
                                                  };

                                                  return (
                                                       <CForm id="gform_84" className="footer-gform" noValidate onSubmit={handleSubmit}>
                                                            <div className="form-group">
                                                                 <CInputGroup>
                                                                      <CFormInput
                                                                           id="input_84_1"
                                                                           name="email"
                                                                           type="text"
                                                                           placeholder="Enter email"
                                                                           className="footer-gform-email"
                                                                           autoComplete="off"
                                                                           invalid={!!errors.email}
                                                                           disabled={isSubmitting}
                                                                           value={values.email}
                                                                           onChange={handleChangeEmail}
                                                                      />
                                                                      <CFormFeedback invalid>{errors.email}</CFormFeedback>
                                                                 </CInputGroup>
                                                                 <Button
                                                                      type="submit"
                                                                      className={classNames('btn-primary-v2 footer-gform-submit', {
                                                                           'btn-disabled': isSubmitting,
                                                                      })}
                                                                      loading={isSubmitting}
                                                                 >
                                                                      Sign Up
                                                                 </Button>
                                                            </div>
                                                       </CForm>
                                                  );
                                             }}
                                        </Formik>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="footer-under">
                         <div className="copyright">
                              <p>
                                   © All Rights Reserved &nbsp;| &nbsp;
                                   <a
                                        href={`${CAMS_CARE_URL}privacy-policy/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-link-item item-privacy-policy"
                                   >
                                        Privacy Policy
                                   </a>{' '}
                                   &nbsp;| &nbsp;
                                   <a
                                        href={`${CAMS_CARE_URL}terms-of-use/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-link-item item-term"
                                   >
                                        Terms & Condition
                                   </a>
                              </p>
                         </div>
                         <div className="footer-socials">
                              <div className="footer-socials-subnav">
                                   <span className="footer-socials-follow">Follow Us:</span>
                                   <a
                                        href="https://twitter.com/CAMScare_tweets"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-socials-item"
                                   >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                             <path
                                                  d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"
                                                  fill="currentColor"
                                             />
                                        </svg>
                                   </a>
                                   <a
                                        href="https://www.facebook.com/camscaretraining/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-socials-item"
                                   >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                             <path
                                                  d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
                                                  fill="currentColor"
                                             />
                                        </svg>
                                   </a>
                                   <a
                                        href="https://www.linkedin.com/company/cams-care/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-socials-item"
                                   >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                             <path
                                                  d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"
                                                  fill="currentColor"
                                             />
                                        </svg>
                                   </a>
                              </div>
                         </div>
                    </div>
               </div>
          </footer>
     );
};

const mapStateToProps = (state) => {
     return {
          general: state.general,
          subscriber: state.subscriber,
     };
};

const mapDispatchToProps = {
     toggleTheme,
     setSidebarShow,
};

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(GuestFooter));
